<template>
  <v-app>
    <v-main class="text-start">
      <v-container fluid>
        <table v-if="patient" class="print-table" style="width: 100%!important; table-layout: fixed;">
          <thead>
            <tr>
              <td>
                <h3>
                  {{ $t('app.settings.general.export-1a') }} {{ patient.firstName }} {{ patient.lastName }}
                </h3>
                <span class="grey--text">
                  {{ new Date() | moment("D MMMM YY - HH:mm") }}
                </span>
                <v-divider class="my-3"></v-divider>
              </td>
            </tr>
          </thead>
          <tbody v-if="posts">
            <tr v-for="(post, postIndex) in posts" :key="post.id || postIndex">
              <td class="post-td" style="padding: 0; width: 100%;">
                <v-card outlined class="my-3" style="max-width: 100%;">
                  <div v-if="post.milestone">
                    <Milestone
                      :milestone="post.milestone"
                      :date="post.createdAt"
                      :image="post.upload ? post.upload : null"
                    />
                  </div>

                  <v-card-title
                    v-if="!post.milestone"
                    class="print-wrap-text"
                    style="word-break: break-word; word-wrap: break-word; overflow-wrap: break-word; white-space: pre-wrap;"
                  >{{ post.title }}</v-card-title>

                  <v-card-subtitle>
                    {{ post.createdAt | moment("D MMMM YY - HH:mm") }} | {{ post.user.firstName }}
                  </v-card-subtitle>

                  <v-card-text
                    v-if="postContent(post)"
                    class="black--text print-wrap-html"
                    style="word-break: break-word; word-wrap: break-word; overflow-wrap: break-word;"
                    v-html="postContent(post)"
                  />

                  <v-card-text v-if="post.uploadId && post.upload">
                    <template v-if="isVideo(post.upload)">
                      <!-- Successful canvas capture -->
                      <img
                        v-if="videoThumbnails[post.upload.id]"
                        :src="videoThumbnails[post.upload.id]"
                        :alt="'Video: ' + post.title"
                        class="upload-media"
                      />
                      <!-- Capture failed — show a clean branded placeholder -->
                      <div v-else class="video-fallback">
                        <div class="video-fallback__icon-area">
                          <div class="video-fallback__icon">
                            <svg viewBox="0 0 24 24">
                              <polygon points="23 7 16 12 23 17 23 7"></polygon>
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                            </svg>
                          </div>
                        </div>
                        <div class="video-fallback__meta">
                          <span class="video-fallback__name">{{ post.upload.originalName || post.title }}</span>
                          <span class="video-fallback__type">{{ (post.upload.mimeType || 'video').replace('video/', '') }}</span>
                        </div>
                      </div>
                    </template>

                    <img
                      v-else
                      :src="post.upload.blobUri"
                      :alt="'Post ' + post.title + '.'"
                      class="upload-media"
                    />
                  </v-card-text>

                  <v-card-text v-if="post.likes || post.comments">
                    <template v-if="post.likes">
                      <h3>{{ post.likes.length }} {{ post.likes.length !== 1 ? $t('general.feed.like.multiple-likes') : $t('general.feed.like.one-like') }}</h3>
                      <div class="mb-3">
                        <div class="grey--text" v-for="like in post.likes" :key="like.id">
                          {{ like.user.firstName }}
                        </div>
                      </div>
                    </template>

                    <template v-if="post.comments">
                      <h3>{{ post.comments.length }} {{ post.comments.length !== 1 ? $t('general.feed.comment.multiple-comments') : $t('general.feed.comment.one-comment') }}</h3>
                      <div v-for="comment in post.comments" :key="comment.id">
                        <span class="grey--text">{{ comment.createdAt | moment("D MMMM YY - HH:mm") }} | {{ comment.user.firstName }}</span>
                        <p class="print-wrap-text" style="word-break: break-word; word-wrap: break-word; overflow-wrap: break-word; white-space: pre-wrap;">{{ comment.content }}</p>
                      </div>
                    </template>
                  </v-card-text>
                </v-card>
              </td>
            </tr>
          </tbody>
        </table>
      </v-container>

      <div class="print-footer">
        <span>{{ $t('text.teVroegNotice') }}
          <a href="https://www.tevroeg.be">www.tevroeg.be</a>
        </span>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import i18n from "@/services/i18n";

export default {
  i18n: i18n,
  name: "neoparent-print",
  props: ["state", "lang"],
  data() {
    return {
      patient: null,
      posts: [],
      // upload.id -> base64 JPEG data URL
      videoThumbnails: {}
    };
  },
  async mounted() {
    window.__videoThumbnailsReady = false;

    this.$i18n.locale = this.lang;

    try {
      const parsed = JSON.parse(this.state);
      this.patient = parsed.patient;
      this.posts = parsed.posts || [];
    } catch (e) {
      console.error("Failed to parse print state:", e);
      window.__videoThumbnailsReady = true;
      return;
    }

    await Promise.all([
      this.waitForPostImagesReady(),
      this.captureAllVideoThumbnails()
    ]);

    // Let Vue flush thumbnail/image updates before Puppeteer captures the page.
    await this.$nextTick();
    await this.waitForPaint();

    window.__videoThumbnailsReady = true;
  },
  methods: {
    isVideo(upload) {
      return Boolean(upload?.mimeType?.toLowerCase().startsWith("video"));
    },

    postContent(post) {
      return (post.reflection?.content) || post.content || "";
    },

    async waitForPostImagesReady() {
      const imageUrls = this.posts
        .map(post => post.upload)
        .filter(upload => upload && !this.isVideo(upload) && upload.blobUri)
        .map(upload => upload.blobUri);

      if (!imageUrls.length) {
        return;
      }

      const uniqueUrls = [...new Set(imageUrls)];
      await Promise.all(uniqueUrls.map(url => this.waitForImageLoad(url)));
    },

    waitForImageLoad(url) {
      return new Promise(resolve => {
        const IMAGE_TIMEOUT_MS = 15000;
        const image = new Image();
        let settled = false;

        const done = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timerId);
          image.onload = null;
          image.onerror = null;
          resolve();
        };

        const timerId = setTimeout(done, IMAGE_TIMEOUT_MS);
        image.onload = done;
        image.onerror = done;
        image.src = url;

        if (image.complete) {
          done();
        }
      });
    },

    waitForPaint() {
      return new Promise(resolve => {
        if (typeof window.requestAnimationFrame !== "function") {
          setTimeout(resolve, 0);
          return;
        }

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });
    },

    async captureAllVideoThumbnails() {
      const videoUploads = this.posts
        .map(p => p.upload)
        .filter(u => u && this.isVideo(u));

      if (!videoUploads.length) return;

      // Run all captures in parallel — each has its own internal timeout
      await Promise.all(videoUploads.map(upload => this.captureOneThumbnail(upload)));
    },

    captureOneThumbnail(upload) {
      return new Promise(resolve => {
        const PER_VIDEO_TIMEOUT_MS = 12000;
        let settled = false;

        const done = (dataUrl) => {
          if (settled) return;
          settled = true;
          clearTimeout(timerId);
          video.src = "";

          if (dataUrl) {
            this.$set(this.videoThumbnails, upload.id, dataUrl);
          }
          resolve();
        };

        const timerId = setTimeout(() => done(null), PER_VIDEO_TIMEOUT_MS);

        const video = document.createElement("video");
        // anonymous crossOrigin + --disable-web-security in Puppeteer allows canvas capture
        // of cross-origin (Azure Blob) video frames without a SecurityError.
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.preload = "auto";

        video.addEventListener("error", () => done(null), { once: true });

        video.addEventListener("loadedmetadata", () => {
          // Seek into the video for a representative, non-black frame.
          // 10 % of duration works well for short clips; capped at 2 s for long videos.
          const seekTo = Math.min(video.duration * 0.1, 2);
          video.currentTime = Math.max(seekTo, 0.5);
        }, { once: true });

        video.addEventListener("seeked", () => {
          const frame = this.drawFrame(video);
          done(frame);
        }, { once: true });

        video.src = upload.blobUri;
        video.load();
      });
    },

    drawFrame(video) {
      if (!video || video.videoWidth === 0 || video.videoHeight === 0) return null;

      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);
        return canvas.toDataURL("image/jpeg", 0.85);
      } catch (e) {
        // SecurityError if CORS canvas taint — fallback placeholder will render instead
        return null;
      }
    }
  }
};
</script>

<style scoped>
.print-table {
  width: 100% !important;
  table-layout: fixed;
  border-collapse: collapse;
}

.print-wrap-text {
  word-break: break-word;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  max-width: 100%;
}

.print-wrap-html {
  word-break: break-word;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  white-space: normal;
}

.print-wrap-html >>> div,
.print-wrap-html >>> p,
.print-wrap-html >>> span,
.print-wrap-html >>> a,
.print-wrap-html >>> li,
.print-wrap-html >>> ul,
.print-wrap-html >>> ol,
.print-wrap-html >>> h1,
.print-wrap-html >>> h2,
.print-wrap-html >>> h3,
.print-wrap-html >>> h4,
.print-wrap-html >>> h5,
.print-wrap-html >>> h6,
.print-wrap-html >>> td,
.print-wrap-html >>> th {
  word-break: break-word;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  white-space: normal !important;
  max-width: 100%;
}

td {
  width: 100%;
  max-width: 0;
}

.post-td {
  break-inside: avoid;
  page-break-inside: avoid;
}

.upload-media {
  width: 100%;
  height: 250px;
  object-fit: contain;
  object-position: center;
  display: block;
}

.video-fallback {
  width: 100%;
  height: 220px;
  background: linear-gradient(160deg, #e8f5f6 0%, #d9eef0 100%);
  border-radius: 8px;
  display: grid;
  grid-template-rows: 1fr auto;
  align-items: center;
  overflow: hidden;
  position: relative;
}

.video-fallback__icon-area {
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-fallback__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(0, 169, 179, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-fallback__icon svg {
  width: 26px;
  height: 26px;
  color: #00a9b3;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.video-fallback__meta {
  background: rgba(255, 255, 255, 0.72);
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.video-fallback__name {
  font-size: 13px;
  font-weight: 600;
  color: #24424a;
  word-break: break-all;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.video-fallback__type {
  font-size: 11px;
  color: #00a9b3;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.print-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid #ddd;
  background: #fff;
  font-size: 13px;
  color: #666;
}

.print-footer a {
  color: #0000EE !important;
  text-decoration: underline !important;
}
</style>

<style>
@page {
  margin-bottom: 50px;
}
</style>